import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Star } from "lucide-react";

const T = {
  title: { en: "Profile", ru: "Профиль", lv: "Profils" },
  name: { en: "Full name", ru: "Имя", lv: "Vārds, uzvārds" },
  phone: { en: "Phone", ru: "Телефон", lv: "Tālrunis" },
  email: { en: "Email", ru: "Email", lv: "E-pasts" },
  save: { en: "Save changes", ru: "Сохранить", lv: "Saglabāt" },
  saving: { en: "Saving...", ru: "Сохранение...", lv: "Saglabā..." },
  saved: { en: "Profile saved", ru: "Профиль сохранён", lv: "Profils saglabāts" },
  failed: { en: "Could not save profile", ru: "Не удалось сохранить", lv: "Neizdevās saglabāt" },
  nameRequired: { en: "Name is required", ru: "Укажите имя", lv: "Norādiet vārdu" },
  addresses: { en: "Saved addresses", ru: "Сохранённые адреса", lv: "Saglabātās adreses" },
  noAddresses: { en: "No saved addresses yet", ru: "Пока нет сохранённых адресов", lv: "Vēl nav saglabātu adrešu" },
  addPlaceholder: { en: "Street, house number, Liepāja", ru: "Улица, дом, Лиепая", lv: "Iela, mājas numurs, Liepāja" },
  add: { en: "Add", ru: "Добавить", lv: "Pievienot" },
  default: { en: "Default", ru: "По умолчанию", lv: "Noklusējuma" },
  makeDefault: { en: "Make default", ru: "Сделать основным", lv: "Iestatīt kā noklusējuma" },
  remove: { en: "Remove", ru: "Удалить", lv: "Dzēst" },
};

export const ProfileEditor = ({ language = "lv" }) => {
  const { user, updateProfile } = useAuth();
  const t = (k) => T[k][language] || T[k].lv;

  const [fullName, setFullName] = useState(user.full_name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [addresses, setAddresses] = useState(user.addresses || []);
  const [defaultAddress, setDefaultAddress] = useState(user.default_delivery_address || "");
  const [newAddress, setNewAddress] = useState("");
  const [saving, setSaving] = useState(false);

  const persist = async (payload) => {
    setSaving(true);
    const res = await updateProfile(payload);
    setSaving(false);
    if (res.success) {
      toast.success(t("saved"));
      setAddresses(res.user.addresses || []);
      setDefaultAddress(res.user.default_delivery_address || "");
    } else {
      toast.error(res.error || t("failed"));
    }
    return res.success;
  };

  const saveBasics = async () => {
    if (!fullName.trim()) {
      toast.error(t("nameRequired"));
      return;
    }
    await persist({ full_name: fullName.trim(), phone: phone.trim() });
  };

  const addAddress = async () => {
    const addr = newAddress.trim();
    if (!addr || addresses.includes(addr)) return;
    const next = [...addresses, addr];
    const payload = { addresses: next };
    if (!defaultAddress) payload.default_delivery_address = addr;
    if (await persist(payload)) setNewAddress("");
  };

  const removeAddress = async (addr) => {
    const next = addresses.filter((a) => a !== addr);
    const payload = { addresses: next };
    if (defaultAddress === addr) payload.default_delivery_address = next[0] || "";
    await persist(payload);
  };

  const makeDefault = (addr) => persist({ default_delivery_address: addr });

  return (
    <div className="rz-form space-y-16" data-testid="profile-editor">
      <section className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <p className="rz-eyebrow m-0 mb-3">01</p>
          <h2 className="font-display text-[1.9rem] leading-tight text-[#1C1917] m-0">{t("title")}</h2>
        </div>
        <div className="md:col-span-8 md:pl-6 md:border-l border-[#E7E5E4]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label htmlFor="profile-name">{t("name")}</Label>
            <Input id="profile-name" value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="profile-phone">{t("phone")}</Label>
            <Input id="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+371" data-testid="profile-phone-input" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>{t("email")}</Label>
            <Input value={user.email} disabled className="!bg-[#F5F2EC] text-[#78716C]" data-testid="profile-email-input" />
          </div>
        </div>
        <button onClick={saveBasics} disabled={saving} className="rz-btn mt-8" data-testid="profile-save-btn">
          {saving ? t("saving") : t("save")}
        </button>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <p className="rz-eyebrow m-0 mb-3">02</p>
          <h2 className="font-display text-[1.9rem] leading-tight text-[#1C1917] m-0">{t("addresses")}</h2>
        </div>
        <div className="md:col-span-8 md:pl-6 md:border-l border-[#E7E5E4]">

        {addresses.length === 0 ? (
          <p className="text-[#78716C] text-[0.95rem] mb-6" data-testid="profile-no-addresses">{t("noAddresses")}</p>
        ) : (
          <ul className="divide-y divide-[#E7E5E4] border-y border-[#E7E5E4] mb-6" data-testid="profile-address-list">
            {addresses.map((addr) => {
              const isDefault = addr === defaultAddress;
              return (
                <li
                  key={addr}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4"
                  data-testid="profile-address-item"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin className="w-4 h-4 text-[#A8A29E] shrink-0" strokeWidth={1.5} />
                    <span className="truncate text-[#1C1917]">{addr}</span>
                    {isDefault && (
                      <span className="inline-flex items-center gap-1.5 text-[0.62rem] font-medium tracking-[0.18em] uppercase text-[#1C1917] border border-[#1C1917] px-2 py-1 shrink-0" data-testid="profile-default-badge">
                        <Star className="w-3 h-3" strokeWidth={1.5} /> {t("default")}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {!isDefault && (
                      <button disabled={saving} onClick={() => makeDefault(addr)} className="rz-btn rz-btn--outline rz-btn--sm" data-testid="profile-make-default-btn">
                        {t("makeDefault")}
                      </button>
                    )}
                    <button disabled={saving} onClick={() => removeAddress(addr)} className="min-w-[44px] min-h-[44px] inline-flex items-center justify-center text-[#A8A29E] hover:text-[#C2410C] transition-colors" aria-label={t("remove")} data-testid="profile-remove-address-btn">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addAddress()}
            placeholder={t("addPlaceholder")}
            data-testid="profile-new-address-input"
          />
          <button onClick={addAddress} disabled={saving || !newAddress.trim()} className="rz-btn rz-btn--outline shrink-0" data-testid="profile-add-address-btn">
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            {t("add")}
          </button>
        </div>
        </div>
      </section>
    </div>
  );
};
